import React, { useState, useEffect, useCallback, useRef } from "react";
import styled from "styled-components";
import { motion } from "framer-motion";
import { ConnectButton } from "@rainbow-me/rainbowkit";
import { useAccount, useDisconnect, useSwitchChain } from "wagmi";
import {
  getExchanges,
  getCoins,
  getPositions,
  connectWallet,
  subscribeToPositionUpdates,
  subscribeToAllPriceUpdates,
  getAuthToken,
} from "./services/api";
import ExchangeSelector from "./components/ExchangeSelector";
import CoinSelector from "./components/CoinSelector";
import TradeForm from "./components/TradeForm";
import PositionsList from "./components/PositionsList";
import UserWalletManager from "./components/UserWalletManager";

import GlobalStyles from "./styles/GlobalStyles";

import { DynamicThemeProvider } from "./contexts/ThemeContext";
import Container from "./components/ui/Container";
import Card from "./components/ui/Card";
import Header from "./components/layout/Header";

import { WalletProvider } from "./contexts/WalletContext";

const ContentContainer = styled(motion.div)`
  margin-top: ${(props) => props.theme.spacing.xl};
  display: flex;
  flex-direction: column;
  gap: ${(props) => props.theme.spacing.xl};
`;

const WelcomeMessage = styled(motion.div)`
  text-align: center;
  max-width: 600px;
  margin: ${(props) => props.theme.spacing.xl} auto
    ${(props) => props.theme.spacing.lg};

  h1 {
    font-size: 2.5rem;
    background: linear-gradient(
      90deg,
      ${(props) => props.theme.colors.accent},
      ${(props) => props.theme.colors.secondary}
    );
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    margin-bottom: ${(props) => props.theme.spacing.lg};
  }

  p {
    color: ${(props) => props.theme.colors.text.secondary};
    font-size: ${(props) => props.theme.fontSize.lg};
    line-height: 1.6;
  }
`;

const ErrorMessage = styled(motion.div)`
  background-color: rgba(255, 94, 91, 0.1);
  border: 1px solid ${(props) => props.theme.colors.status.error};
  border-radius: ${(props) => props.theme.borderRadius.md};
  padding: ${(props) => props.theme.spacing.md};
  margin-bottom: ${(props) => props.theme.spacing.lg};
  color: ${(props) => props.theme.colors.status.error};
  text-align: center;
`;

function App() {
  const [wallet, setWallet] = useState(null);
  const [selectedExchange, setSelectedExchange] = useState(null);
  const [coins, setCoins] = useState(() => {
    const saved = localStorage.getItem("selectedCoins");
    return saved ? JSON.parse(saved) : [];
  });
  const [selectedCoin, setSelectedCoin] = useState(() => {
    const saved = localStorage.getItem("selectedCoin");
    return saved ? JSON.parse(saved) : null;
  });
  const [positions, setPositions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isAuthenticating, setIsAuthenticating] = useState(false);
  const [error, setError] = useState(null);
  const [exchangesLoaded, setExchangesLoaded] = useState(false);
  const sseConnectionRef = useRef(null);
  const priceSseConnectionRef = useRef(null);
  // eslint-disable-next-line no-unused-vars
  const [userHasClickedConnect, setUserHasClickedConnect] = useState(false);
  const walletConnectionHandlerRef = useRef();

  // RainbowKit hooks for wallet detection
  const { address, isConnected, chainId } = useAccount();
  const { disconnect } = useDisconnect();
  const { switchChain } = useSwitchChain();

  // Treat exchange as selected only when it matches supported ids
  const isExchangeSelected =
    selectedExchange === "pancakeswap" || selectedExchange === "hyperliquid";

  // Persist trading setup state to localStorage with timestamps
  const persistTradingSetup = useCallback((exchange, coin, coinsList) => {
    if (exchange) localStorage.setItem("selectedExchange", exchange);
    if (coin) {
      // Add timestamp to coin data for validation
      const coinWithTimestamp = {
        ...coin,
        last_updated: new Date().toISOString(),
      };
      localStorage.setItem("selectedCoin", JSON.stringify(coinWithTimestamp));
    }
    if (coinsList && coinsList.length > 0) {
      // Add timestamps to coins data for validation
      const coinsWithTimestamps = coinsList.map((coin) => ({
        ...coin,
        last_updated: new Date().toISOString(),
      }));
      localStorage.setItem(
        "selectedCoins",
        JSON.stringify(coinsWithTimestamps),
      );
    }
  }, []);

  // Clear trading setup state from localStorage
  const clearTradingSetup = useCallback(() => {
    localStorage.removeItem("selectedExchange");
    localStorage.removeItem("selectedCoin");
    localStorage.removeItem("selectedCoins");
  }, []);

  // Restore trading setup from localStorage with validation
  useEffect(() => {
    // Restore basic setup even without wallet connection
    const savedExchange = localStorage.getItem("selectedExchange");
    const savedCoin = localStorage.getItem("selectedCoin");
    const savedCoins = localStorage.getItem("selectedCoins");

    if (savedExchange && (savedCoin || savedCoins)) {
      // Validate that saved data is not too old (24 hours)
      const isDataFresh =
        savedCoin &&
        (() => {
          try {
            const parsedCoin = JSON.parse(savedCoin);
            return (
              parsedCoin.last_updated &&
              Date.now() - new Date(parsedCoin.last_updated).getTime() <
                24 * 60 * 60 * 1000
            );
          } catch (e) {
            return false;
          }
        })();

      if (isDataFresh) {
        // Set the exchange immediately (will be validated when exchanges load)
        setSelectedExchange(savedExchange);

        // Restore coins with validation
        if (savedCoins) {
          try {
            const parsedCoins = JSON.parse(savedCoins);
            if (Array.isArray(parsedCoins) && parsedCoins.length > 0) {
              // Filter out invalid coins
              const validCoins = parsedCoins.filter(
                (coin) =>
                  coin &&
                  coin.address &&
                  coin.symbol &&
                  coin.dex_name === savedExchange,
              );
              if (validCoins.length > 0) {
                setCoins(validCoins);
                console.log(
                  `Restored ${validCoins.length} valid coins from localStorage`,
                );
              } else {
                console.log("No valid coins found in localStorage, clearing");
                localStorage.removeItem("selectedCoins");
              }
            }
          } catch (e) {
            console.error("Error parsing saved coins:", e);
            localStorage.removeItem("selectedCoins");
          }
        }

        // Restore selected coin - only if we have coins data
        if (savedCoin) {
          try {
            const parsedCoin = JSON.parse(savedCoin);
            // Store the parsed coin to restore later when coins are loaded
            setSelectedCoin(parsedCoin);
          } catch (e) {
            console.error("Error parsing saved coin:", e);
            localStorage.removeItem("selectedCoin");
          }
        }
      } else {
        console.log("Saved trading setup is outdated, clearing localStorage");
        localStorage.removeItem("selectedExchange");
        localStorage.removeItem("selectedCoin");
        localStorage.removeItem("selectedCoins");
      }
    }
  }, []); // Run only once on mount

  // Restore selected coin once coins are loaded
  useEffect(() => {
    if (
      coins.length > 0 &&
      selectedCoin &&
      typeof selectedCoin === "object" &&
      selectedCoin.address
    ) {
      // Find the actual coin object from the loaded coins
      const foundCoin = coins.find(
        (coin) =>
          coin.address === selectedCoin.address ||
          coin.symbol === selectedCoin.symbol,
      );
      if (foundCoin) {
        setSelectedCoin(foundCoin);
      }
    }
  }, [coins, selectedCoin]);

  // Handle wallet connection from RainbowKit
  const handleWalletConnectedFromRainbowKit = useCallback(
    async (walletData) => {
      // Prevent duplicate authentication calls
      if (isAuthenticating) {
        console.log(
          "Authentication already in progress, skipping duplicate call",
        );
        return;
      }

      // Check if we already have this wallet connected
      if (wallet && wallet.address === walletData.address) {
        console.log(
          "Wallet already connected with same address, skipping authentication",
        );
        return;
      }

      try {
        setIsAuthenticating(true);
        setLoading(true);

        console.log("Starting wallet authentication for:", walletData.address);

        // Force BSC network for PancakeSwap
        const targetNetworkId = "56"; // BSC

        // If user is on a different network, try to switch to BSC
        if (walletData.chainId !== 56) {
          try {
            console.log("Switching to BSC network...");
            await switchChain({ chainId: 56 });
            console.log("Successfully switched to BSC network");
          } catch (error) {
            console.warn("Failed to switch to BSC network:", error);
          }
        }

        const result = await connectWallet(
          walletData.address,
          null,
          targetNetworkId, // Always use BSC
        );

        if (result.success) {
          setWallet({ ...result.wallet, network_id: "56" }); // Always use BSC
          // Clear any previous errors on successful connection
          setError(null);

          // Always use PancakeSwap for BSC network
          setSelectedExchange("pancakeswap");

          // Load coins for PancakeSwap
          try {
            const coinsData = await getCoins("pancakeswap");
            setCoins(coinsData);
          } catch (error) {
            console.error("Error loading coins for PancakeSwap:", error);
            setError("Failed to load coins for PancakeSwap");
          }

          // Ensure positions are fetched immediately after authentication
          try {
            const positionsData = await getPositions(walletData.address, targetNetworkId);
            if (
              positionsData &&
              positionsData.positions &&
              Array.isArray(positionsData.positions)
            ) {
              setPositions(positionsData.positions);
            }
          } catch (_) {}
        } else {
          // Handle connection failure
          console.error("Wallet connection failed:", result.error);
          setError(result.error || "Failed to connect wallet");
          // Don't proceed with loading coins or other operations
          return;
        }
      } catch (error) {
        console.error("Error connecting wallet from RainbowKit:", error);
        setError("Failed to connect wallet");
      } finally {
        setLoading(false);
        setIsAuthenticating(false);
      }
    },
    [isAuthenticating, wallet, switchChain],
  );

  // Update the ref when the function changes
  useEffect(() => {
    walletConnectionHandlerRef.current = handleWalletConnectedFromRainbowKit;
  }, [handleWalletConnectedFromRainbowKit]);

  // Handle wallet disconnection - completely independent of wallet state
  const handleDisconnectWallet = useCallback(async () => {
    try {
      setLoading(true);
      setIsAuthenticating(false); // Reset authentication state
      // Disconnect from RainbowKit/wagmi
      await disconnect();

      // Clear local state
      setWallet(null);

      // Clear localStorage and trading setup
      localStorage.removeItem("walletAddress");
      clearTradingSetup();

      // Reset exchanges loaded flag
      setExchangesLoaded(false);

      // Close SSE connection
      if (
        sseConnectionRef.current &&
        typeof sseConnectionRef.current.close === "function"
      ) {
        sseConnectionRef.current.close();
        sseConnectionRef.current = null;
      }

      if (
        priceSseConnectionRef.current &&
        typeof priceSseConnectionRef.current.close === "function"
      ) {
        priceSseConnectionRef.current.close();
        priceSseConnectionRef.current = null;
      }
    } catch (error) {
      console.error("Error disconnecting wallet:", error);
    } finally {
      setLoading(false);
    }
  }, [clearTradingSetup, disconnect]);

  // Handle chain changes
  const handleChainChanged = useCallback(
    async (chainId) => {
      try {
        setLoading(true);
        // Check if the new network matches the current wallet
        if (String(wallet?.network_id) === "56") {
          return;
        }

        // Network changed, update wallet connection
        const result = await connectWallet(
          wallet?.address,
          null,
          String(chainId),
        );
        if (result.success) {
          // Update wallet state
          setWallet({ ...result.wallet, network_id: "56" }); // Always use BSC
          // Clear any previous errors on successful connection
          setError(null);

          // Always use PancakeSwap for BSC network
          setSelectedExchange("pancakeswap");

          // Load coins for PancakeSwap
          try {
            const coinsData = await getCoins("pancakeswap");
            setCoins(coinsData);
          } catch (error) {
            console.error("Error loading coins for PancakeSwap:", error);
            setError("Failed to load coins for PancakeSwap");
          }

           // Reload positions on chain change as well
           try {
             const addr = result?.wallet?.address || wallet?.address;
             if (addr) {
               const positionsData = await getPositions(addr, "56");
               if (
                 positionsData &&
                 positionsData.positions &&
                 Array.isArray(positionsData.positions)
               ) {
                 setPositions(positionsData.positions);
               }
             }
           } catch (_) {}
        } else {
          // Handle connection failure
          console.error("Chain change connection failed:", result.error);
          setError(result.error || "Failed to connect wallet on chain change");
          // Don't proceed with loading coins or other operations
          return;
        }
      } catch (error) {
        console.error("Error handling chain change:", error);
        setError("Failed to handle chain change");
      } finally {
        setLoading(false);
      }
    },
    [wallet],
  );

  useEffect(() => {
    if (!isConnected && wallet) {
      console.log("RainbowKit disconnected - clearing wallet state");
      setWallet(null);
      setIsAuthenticating(false);
      setUserHasClickedConnect(false);
    }
  }, [isConnected, wallet]);

  useEffect(() => {
    const syncWallet = async () => {
      if (
        !address ||
        !isConnected ||
        isAuthenticating ||
        !userHasClickedConnect
      )
        return;
      setIsAuthenticating(true);
      try {
        if (walletConnectionHandlerRef.current) {
          await walletConnectionHandlerRef.current({ address, chainId });
        }
        setError(null);
      } catch (err) {
        console.error("Wallet sync failed:", err);
      } finally {
        setUserHasClickedConnect(false);
        setIsAuthenticating(false);
      }
    };

    // Only sync if we don't already have a wallet with this address AND user clicked connect
    if ((!wallet || wallet.address !== address) && userHasClickedConnect) {
      syncWallet();
    }
  }, [
    address,
    isConnected,
    chainId,
    isAuthenticating,
    wallet,
    userHasClickedConnect,
  ]);

  // Handle chain changes
  useEffect(() => {
    // Always use BSC network, no need to handle chain changes
    return;
  }, [chainId, wallet?.network_id, handleChainChanged]);

  // Handle wallet connection (positions SSE + initial load)
  useEffect(() => {
    if (!wallet?.address || !isConnected || !getAuthToken()) {
      return;
    }

    console.log("Setting up position SSE for wallet:", wallet.address);

    // Load existing positions first
    const loadInitialPositions = async () => {
      try {
        const positionsData = await getPositions(
          wallet.address,
          wallet.chainId,
        );
        if (
          positionsData &&
          positionsData.positions &&
          Array.isArray(positionsData.positions)
        ) {
          setPositions(positionsData.positions);
          console.log(
            `Loaded ${positionsData.positions.length} existing positions`,
          );
        }
      } catch (error) {
        console.error("Error loading initial positions:", error);
      }
    };

    loadInitialPositions();

    // Set up SSE for position updates
    const setupSSE = async () => {
      const newSseConnection = await subscribeToPositionUpdates(
        wallet.address,
        wallet.chainId,
        (data) => {
          if (Array.isArray(data)) {
            setPositions(data);
          } else if (data.positions && Array.isArray(data.positions)) {
            setPositions(data.positions);
          }
        },
      );
      sseConnectionRef.current = newSseConnection;
    };

    setupSSE();

    // Cleanup function
    return () => {
      console.log("Cleaning up position SSE");
      if (
        sseConnectionRef.current &&
        typeof sseConnectionRef.current.close === "function"
      ) {
        sseConnectionRef.current.close();
      }
      sseConnectionRef.current = null;
    };
  }, [wallet?.address, wallet?.chainId, isConnected]);

  // Handle exchange changes (price SSE only)
  useEffect(() => {
    if (
      !wallet?.address ||
      !isConnected ||
      !getAuthToken() ||
      !selectedExchange
    ) {
      return;
    }

    console.log("Setting up price SSE for exchange:", selectedExchange);

    // Set up SSE for all price updates
    const newPriceSseConnection = subscribeToAllPriceUpdates(
      selectedExchange.toLowerCase(),
      (token, price) => {
        // Update coin price if it's in our coins list
        setCoins((prevCoins) =>
          prevCoins.map((coin) =>
            coin.address === token ? { ...coin, price } : coin,
          ),
        );
      },
      (err) => console.error("Prices SSE error:", err),
      (token, volume) => {
        // Update coin volume if it's in our coins list
        setCoins((prevCoins) =>
          prevCoins.map((coin) =>
            coin.address === token ? { ...coin, volume } : coin,
          ),
        );
      },
    );

    priceSseConnectionRef.current = newPriceSseConnection;

    // Cleanup function
    return () => {
      console.log("Cleaning up price SSE for exchange:", selectedExchange);
      if (
        newPriceSseConnection &&
        typeof newPriceSseConnection.close === "function"
      ) {
        newPriceSseConnection.close();
      }
      if (priceSseConnectionRef.current) {
        priceSseConnectionRef.current.close();
        priceSseConnectionRef.current = null;
      }
    };
  }, [selectedExchange, wallet?.address, isConnected]);

  // Handle exchange changes
  const handleExchangeChange = useCallback(async (exchange) => {
    try {
      setLoading(true);

      // Clean up existing SSE connections before switching
      if (priceSseConnectionRef.current) {
        priceSseConnectionRef.current.close();
        priceSseConnectionRef.current = null;
      }

      setSelectedExchange(exchange);
      localStorage.setItem("selectedExchange", exchange);

      // Load coins for the new exchange
      const coinsData = await getCoins(exchange);
      setCoins(coinsData);

      // Clear selected coin when switching exchanges
      setSelectedCoin(null);
      localStorage.removeItem("selectedCoin");
    } catch (error) {
      console.error(`❌ Error switching to ${exchange}:`, error);
      setError(`Failed to load coins for ${exchange}`);
    } finally {
      setLoading(false);
    }
  }, []);

  // Consolidated useEffect for initial data loading and exchange selection
  useEffect(() => {
    if (
      !exchangesLoaded &&
      wallet?.address &&
      isConnected &&
      !selectedExchange
    ) {
      const preloadData = async () => {
        try {
          setLoading(true);
          // Load exchanges
          const exchangesData = await getExchanges();

          // Load coins for the first exchange
          if (exchangesData.length > 0) {
            // Always use PancakeSwap for BSC network
            setSelectedExchange("pancakeswap");

            const coinsData = await getCoins("pancakeswap");
            setCoins(coinsData);
          }

          setExchangesLoaded(true);
        } catch (error) {
          console.error("❌ Error preloading data:", error);
          setError("Failed to load exchanges and coins");
        } finally {
          setLoading(false);
        }
      };

      preloadData();
    }
  }, [exchangesLoaded, wallet?.address, isConnected, selectedExchange]);

  // Restore saved coins from localStorage only when needed
  useEffect(() => {
    if (
      selectedExchange &&
      coins.length === 0 &&
      !error &&
      wallet &&
      !loading
    ) {
      const savedCoins = localStorage.getItem("selectedCoins");
      if (savedCoins) {
        try {
          const parsedCoins = JSON.parse(savedCoins);
          // Validate that saved coins are still valid for current exchange
          if (Array.isArray(parsedCoins) && parsedCoins.length > 0) {
            // Check if saved coins have required fields and are not too old
            const validCoins = parsedCoins.filter(
              (coin) =>
                coin &&
                coin.address &&
                coin.symbol &&
                coin.dex_name === selectedExchange &&
                coin.last_updated &&
                Date.now() - new Date(coin.last_updated).getTime() <
                  24 * 60 * 60 * 1000, // 24 hours
            );

            if (validCoins.length > 0) {
              console.log(
                `Restored ${validCoins.length} valid coins from localStorage`,
              );
              setCoins(validCoins);
            } else {
              console.log(
                "Saved coins are outdated or invalid, will fetch fresh data",
              );
              localStorage.removeItem("selectedCoins");
            }
          }
        } catch (e) {
          console.error("Error parsing saved coins:", e);
          localStorage.removeItem("selectedCoins");
        }
      }
    }
  }, [selectedExchange, error, wallet, loading, coins.length]);

  // Handle coin selection
  const handleCoinSelect = useCallback(
    (coin) => {
      setSelectedCoin(coin);
      localStorage.setItem("selectedCoin", JSON.stringify(coin));
      persistTradingSetup(selectedExchange, coin, coins);
    },
    [selectedExchange, coins, persistTradingSetup],
  );

  // Handle position creation: sync from backend after creation/update
  const handlePositionCreated = useCallback(
    async (position) => {
      if (position && typeof position === "object") {
        setPositions((prev) => {
          const index = Array.isArray(prev)
            ? prev.findIndex((p) => p && p.id === position.id)
            : -1;
          if (index >= 0) {
            const updated = [...prev];
            updated[index] = { ...updated[index], ...position };
            return updated;
          }
          return [position, ...(Array.isArray(prev) ? prev : [])];
        });
      }

      if (wallet?.address && isConnected) {
        try {
          const positionsData = await getPositions(
            wallet.address,
            wallet.chainId,
          );
          if (
            positionsData &&
            positionsData.positions &&
            Array.isArray(positionsData.positions)
          ) {
            setPositions(positionsData.positions);
          }
        } catch (_) {
          // ignore sync errors; SSE/next refresh will reconcile
        }
      }
     },
     [wallet?.address, wallet?.chainId, isConnected],
   );

  // Handle refresh wallet connection
  const handleRefreshWalletConnection = useCallback(() => {
    // This would handle refreshing the wallet connection
  }, []);

  // Manual position loading function - called when user explicitly wants to see positions
  const handleLoadPositions = useCallback(async () => {
    if (!wallet?.address || !isConnected) {
      setError("Wallet must be connected to load positions");
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const positionsData = await getPositions(wallet.address, wallet.chainId);

      if (
        positionsData &&
        positionsData.positions &&
        Array.isArray(positionsData.positions)
      ) {
        setPositions(positionsData.positions);
        console.log(`Loaded ${positionsData.positions.length} positions`);
      } else {
        setPositions([]);
        console.log("No positions found");
      }
    } catch (error) {
      console.error("Error loading positions:", error);
      setError("Failed to load positions");
      setPositions([]);
    } finally {
      setLoading(false);
    }
  }, [wallet?.address, wallet?.chainId, isConnected]);

  // Clear error when component unmounts or dependencies change
  useEffect(() => {
    return () => {
      setError(null);
    };
  }, []);

  // Cleanup all SSE connections on unmount
  useEffect(() => {
    return () => {
      if (sseConnectionRef.current) {
        sseConnectionRef.current.close();
        sseConnectionRef.current = null;
      }
      if (priceSseConnectionRef.current) {
        priceSseConnectionRef.current.close();
        priceSseConnectionRef.current = null;
      }
    };
  }, []);

  // Function to manually clear errors
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  // Clear invalid localStorage data
  const clearInvalidLocalStorage = useCallback(() => {
    try {
      localStorage.removeItem("selectedExchange");
      localStorage.removeItem("selectedCoin");
      localStorage.removeItem("selectedCoins");
      console.log("Cleared invalid localStorage data");
    } catch (e) {
      console.error("Error clearing localStorage:", e);
    }
  }, []);

  // Manual reset for stuck wallet states
  const resetWalletStates = useCallback(() => {
    console.log("Manually resetting wallet states");
    setIsAuthenticating(false);
    setUserHasClickedConnect(false);
    setLoading(false);
    setError(null);
  }, []);

  // Handle token expiration -> force clean logout and prompt re-login
  useEffect(() => {
    const onTokenExpired = () => {
      try {
        console.warn("Auth token expired. Performing clean logout.");
        handleDisconnectWallet();
        setError("Session expired. Please reconnect your wallet.");
      } catch (e) {
        console.error("Error handling token expiration:", e);
      }
    };

    window.addEventListener("tokenExpired", onTokenExpired);
    return () => window.removeEventListener("tokenExpired", onTokenExpired);
  }, [handleDisconnectWallet]);

  return (
    <WalletProvider>
      <DynamicThemeProvider>
        <GlobalStyles />

        {/* Use RainbowKit render prop to access a guaranteed openConnectModal */}
        <ConnectButton.Custom>
          {({ openConnectModal }) => (
            <Header
              wallet={wallet}
              onDisconnect={handleDisconnectWallet}
              onConnectClick={() => {
                setUserHasClickedConnect(true);
                if (typeof openConnectModal === "function") {
                  openConnectModal();
                } else {
                  setError(
                    "Wallet connection modal not ready, please try again",
                  );
                  setUserHasClickedConnect(false);
                }
              }}
              onRefreshWalletConnection={handleRefreshWalletConnection}
              isRainbowKitReady={Boolean(openConnectModal)}
            />
          )}
        </ConnectButton.Custom>

        <Container>
          {error && (
            <ErrorMessage
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  width: "100%",
                }}
              >
                <span>{error}</span>
                <div
                  style={{ display: "flex", gap: "8px", alignItems: "center" }}
                >
                  <button
                    onClick={clearInvalidLocalStorage}
                    style={{
                      background: "#ff6b6b",
                      border: "none",
                      color: "white",
                      cursor: "pointer",
                      fontSize: "12px",
                      padding: "4px 8px",
                      borderRadius: "4px",
                    }}
                    title="Clear invalid cached data"
                  >
                    Clear Cache
                  </button>
                  <button
                    onClick={resetWalletStates}
                    style={{
                      background: "#4ecdc4",
                      border: "none",
                      color: "white",
                      cursor: "pointer",
                      fontSize: "12px",
                      padding: "4px 8px",
                      borderRadius: "4px",
                    }}
                    title="Reset wallet connection states"
                  >
                    Reset Wallet
                  </button>
                  <button
                    onClick={clearError}
                    style={{
                      background: "none",
                      border: "none",
                      color: "inherit",
                      cursor: "pointer",
                      fontSize: "18px",
                      padding: "0 8px",
                    }}
                  >
                    ×
                  </button>
                </div>
              </div>
            </ErrorMessage>
          )}

          <WelcomeMessage
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h1>DEX DCA Trading Bot</h1>
            <p>
              Automate your DCA strategy on decentralized exchanges. Connect
              your wallet to get started with smart, automated trading.
            </p>
          </WelcomeMessage>

          <ContentContainer
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            <Card>
              <h2>Trading Setup</h2>

              <ExchangeSelector
                selectedExchange={selectedExchange}
                onExchangeChange={handleExchangeChange}
              />

              {isExchangeSelected && (
                <CoinSelector
                  coins={coins}
                  loading={loading}
                  selectedCoin={selectedCoin}
                  onCoinSelect={handleCoinSelect}
                  tokenPrices={{}}
                  tokenVolumes={{}}
                  selectedExchange={selectedExchange}
                />
              )}

              {selectedCoin && isExchangeSelected && (
                <ConnectButton.Custom>
                  {({ openConnectModal }) => (
                    <TradeForm
                      wallet={wallet}
                      exchange={selectedExchange}
                      coin={selectedCoin}
                      currentPrice={null}
                      onPositionCreated={handlePositionCreated}
                      onConnectWallet={() => {
                        setUserHasClickedConnect(true);
                        if (typeof openConnectModal === "function") {
                          openConnectModal();
                        } else {
                          setError(
                            "Wallet connection modal not ready, please try again",
                          );
                          setUserHasClickedConnect(false);
                        }
                      }}
                    />
                  )}
                </ConnectButton.Custom>
              )}
            </Card>

            {/* Trading Wallet Card - separate from Trading Setup */}
            {wallet && (
              <Card>
                <UserWalletManager
                  wallet={wallet}
                  networkId={wallet.network_id}
                />
              </Card>
            )}

            {/* Only show positions when wallet is connected AND there are positions */}
            {wallet && (
              <PositionsList
                positions={positions}
                loading={loading}
                walletAddress={wallet.address}
                onPositionUpdate={handleLoadPositions}
              />
            )}
          </ContentContainer>
        </Container>
      </DynamicThemeProvider>
    </WalletProvider>
  );
}

export default App;
